import type { Product } from '@clockwise/clients/products';

/**
 * Can an activity be logged against a given customer?
 *
 * This is the SINGLE rule behind the "change the customer" behavior in the
 * timesheet editors and timer form: an already-selected activity is kept if —
 * and only if — it's still in the new customer's selectable activity set.
 * Otherwise the caller clears the activity (and, for timesheet rows, hours).
 *
 * "In the customer's selectable set" is the exact same membership the activity
 * picker (`ProductAutocomplete` scoped by `customerId` + role filter) and the
 * server's write-time gate (`assertProductValidForEntry` + role resolution)
 * compute. There is nothing customer-change-specific here — internal mismatch
 * and role-not-accessible are not separate behaviors, they're simply two of
 * the reasons an activity falls OUT of the set. The three clauses below ARE the
 * set definition:
 *
 *   1. Custom ownership — a customer-specific activity (`customerId` set) is
 *      only loggable against its own customer; a global activity (`customerId`
 *      null) is loggable against any customer.
 *   2. Internal match — an internal activity is loggable ONLY against the
 *      internal customer, and a non-internal activity NEVER against it.
 *   3. Role access — the assignee must hold a covering team-membership role on
 *      the customer that is in the activity's effective role set
 *      ({serviceRoleId} ∪ visibleToServiceRoleIds).
 *
 * SOW scope is deliberately NOT a clause: out-of-scope activities are still
 * selectable/loggable today (the picker only sections them), so a customer
 * change must keep an otherwise-valid activity even when it's out of the new
 * customer's in-scope SOW set.
 */

/** The customer the activity would move to, plus the assignee's standing on it. */
export interface CustomerActivityScope {
  /** The new customer's id. */
  customerId: string;
  /** Whether the new customer is the internal customer. */
  isInternal: boolean;
  /**
   * Service-role ids the assignee holds (covers) on this customer across the
   * relevant date window — the union of covering `customer_team_members`
   * roles. Empty when no membership covers the customer (role undeterminable),
   * which means no activity is loggable, mirroring the disabled picker.
   */
  coveringRoleIds: readonly string[];
}

/** The fields of an activity the visibility rule reads. */
type VisibilityProduct = Pick<
  Product,
  'customerId' | 'internal' | 'serviceRoleId' | 'visibleToServiceRoleIds'
>;

/**
 * True when `product` is in `scope`'s selectable activity set — i.e. the
 * customer can log against the activity. See the module doc for the three
 * clauses; all must hold.
 */
export function isActivityVisibleForCustomer(
  product: VisibilityProduct,
  scope: CustomerActivityScope,
): boolean {
  // 1. Custom-activity ownership: a pinned activity only its own customer.
  if (product.customerId != null && product.customerId !== scope.customerId) return false;
  // 2. Internal flag must match the customer's internal flag.
  if (product.internal !== scope.isInternal) return false;
  // 3. Role access: a covering role on the customer must be in the activity's
  //    effective role set ({primary} ∪ visibleTo). No covering role → not
  //    loggable (the picker would be disabled for this customer).
  const allowed = new Set<string>([product.serviceRoleId, ...product.visibleToServiceRoleIds]);
  return scope.coveringRoleIds.some((roleId) => allowed.has(roleId));
}
